import { effect, inject, Injectable, Injector, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { DatabaseSyncService } from './database-sync.service';
import {
  ACTIVE_SQL_MODE,
  DATABASE_SERVICE,
  METADATA_KEY_REPOSITORY,
  DATABASE_PERSISTENCE_ADAPTER,
} from '@app/core/tokens/infrastructure.tokens';
import { AccountsStore, AppStore, AuthStore, CategoriesStore } from '@app/core/states';
import { CurrenciesStore } from '@app/ui/stores';
import { DekService } from '@app/domain/services';
import { DekError } from '@app/domain/entities';
import { FeatureFlagsService } from './feature-flags.service';
import { STORAGE_KEYS } from '@app/domain/ports';
import { CRYPTO_ADAPTER } from '@app/core/tokens/infrastructure.tokens';
import { FEATURE_FLAGS } from './feature-flags.constants';
import { AppErrorHandler } from './app-error-handler.service';
import { NotificationService } from './notification.service';
import { TranslateService } from '@ngx-translate/core';
import {
  assertExistingOpfsDatabaseFile,
  collectOpfsDatabaseDiagnostics,
  logOpfsDatabaseDiagnostics,
  toArrayBuffer,
} from '@app/infrastructure/storage';

const DATABASE_MISSING_RECOVERY_SNAPSHOT_ERROR = 'Database missing and no encrypted recovery snapshot found';
const DATABASE_MISSING_RECOVERY_SNAPSHOT_ERROR_CODE = 'E_DATABASE_MISSING_NO_RECOVERY_SNAPSHOT';
export const ERROR_ACTION_REINITIALIZE_DATABASE = 'reinitialize-database';

class DatabaseMissingRecoverySnapshotError extends Error {
  readonly code = DATABASE_MISSING_RECOVERY_SNAPSHOT_ERROR_CODE;

  constructor() {
    super(DATABASE_MISSING_RECOVERY_SNAPSHOT_ERROR);
    this.name = 'DatabaseMissingRecoverySnapshotError';
  }
}

interface Deferred<T> {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
}

function createDeferred<T>(): Deferred<T> {
  let resolve!: (value: T | PromiseLike<T>) => void;
  let reject!: (reason?: unknown) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });

  return { promise, resolve, reject };
}

@Injectable({ providedIn: 'root' })
export class AuthDatabaseInitializer {
  private appStore = inject(AppStore);
  private authStore = inject(AuthStore);
  private databaseService = inject(DATABASE_SERVICE);
  private metadataKeyRepository = inject(METADATA_KEY_REPOSITORY);
  private dbLoader = inject(DATABASE_PERSISTENCE_ADAPTER);
  private dbSyncService = inject(DatabaseSyncService);
  private dekService = inject(DekService);
  private featureFlagsService = inject(FeatureFlagsService);
  private cryptoService = inject(CRYPTO_ADAPTER);
  private injector = inject(Injector);
  private router = inject(Router);
  private sqliteMode = inject(ACTIVE_SQL_MODE);
  private readonly appErrorHandler = inject(AppErrorHandler);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);
  private lastKnownIsUnlocked: boolean | undefined = undefined;
  private databaseInitializationDeferred: Deferred<void> | undefined;

  constructor() {
    effect(() => {
      const isUnlocked = this.appStore.isUnlocked();

      untracked(async () => {
        if (isUnlocked && (!this.databaseService.isInitialized() || !this.appStore.isDatabaseInitialized())) {
          const initializationDeferred = this.ensureDatabaseInitializationDeferred();
          try {
            await this.onDekUnlocked();
            initializationDeferred.resolve();
          } catch (error) {
            initializationDeferred.reject(error);
          } finally {
            this.clearDatabaseInitializationDeferred(initializationDeferred);
          }
        } else if (!isUnlocked && this.databaseService.isInitialized()) {
          await this.onDekLocked();
        }

        await this.tryPersistRawDek();
      }).catch((error) => {
        console.error('Fatal unhandled error in AuthDatabaseInitializer state machine:', error);
      });
    });
  }

  async initialize(): Promise<void> {
    await this.tryAutoLogin();
  }

  private async onDekUnlocked(): Promise<void> {
    try {
      console.log('🔄 DEK unlocked, initializing application...');

      const isDatabaseInitialized = this.databaseService.isInitialized();
      if (isDatabaseInitialized) {
        console.log('📁 Database already initialized. Skipping provider re-initialization.');
      } else {
        let data: Uint8Array | undefined = undefined;
        if (this.sqliteMode !== 'opfs') {
          const loadedData = await this.dbLoader.load();
          if (!loadedData) throw new DekError('initialize-database', 'No database found');
          data = loadedData;
        } else {
          const appInitialized = (await this.metadataKeyRepository.get(STORAGE_KEYS.USER_PROFILE)) !== undefined;
          if (appInitialized) {
            logOpfsDatabaseDiagnostics('auth_unlock_preflight', await collectOpfsDatabaseDiagnostics());
            try {
              const probe = await assertExistingOpfsDatabaseFile();
              console.debug(
                `📁 OPFS mode: Found existing database file (${probe.size ?? 0} bytes, sqlite=${String(probe.headerValid)})`,
              );
            } catch (opfsError) {
              const diagnostics = await collectOpfsDatabaseDiagnostics();
              logOpfsDatabaseDiagnostics('auth_unlock_missing_db', diagnostics, 'warn');
              console.warn(
                '⚠️ OPFS database file is unavailable. Attempting recovery from encrypted snapshot...',
                opfsError,
              );
              const loadedData = await this.dbLoader.load();
              if (!loadedData) {
                throw new DatabaseMissingRecoverySnapshotError();
              }

              data = loadedData;
              console.warn('🛟 OPFS database will be restored from encrypted snapshot');
              this.notifyRecoveredMissingOpfsDatabase(diagnostics.wasDiscarded);
            }
          } else {
            console.debug('📁 OPFS mode: No prior app profile found, allowing fresh database creation');
          }

          // In OPFS mode, OPFS is the authoritative persistence layer.
          // Skip loading from IndexedDB to avoid overwriting the OPFS database.
          console.debug('📁 OPFS mode: Using Origin Private File System as the authoritative database store');
        }

        await this.databaseService.initialize({
          data,
          runMigrations: true,
        });
      }

      this.dbSyncService.startPeriodicSync();
      await this.loadApplicationData();

      this.appStore.markDatabaseInitialized();
      console.log('✓ Application initialized successfully');
    } catch (error) {
      await this.handleInitializationError(error);
      throw error;
    }
  }

  private async onDekLocked(): Promise<void> {
    console.log('🔄 DEK locked, cleaning up resources...');

    this.dbSyncService.stopPeriodicSync();
    await this.databaseService.close();
    this.appStore.markDatabaseUninitialized();

    console.log('✓ Cleanup complete');
  }

  private async tryPersistRawDek() {
    if (!this.featureFlagsService.isEnabled(FEATURE_FLAGS.PERSIST_RAW_DEK)) return;
    const isUnlocked = this.appStore.isUnlocked();
    if (isUnlocked) {
      const dekContainer = this.dekService.getDekContainer();
      await dekContainer.execute(async (dek) => {
        if (dek) {
          const rawDek = await this.cryptoService.exportRawKey(dek);
          await this.metadataKeyRepository.set(STORAGE_KEYS.DEK_RAW, toArrayBuffer(rawDek));
        }
      });
    } else if (this.lastKnownIsUnlocked === true) {
      await this.metadataKeyRepository.delete(STORAGE_KEYS.DEK_RAW);
    }

    this.lastKnownIsUnlocked = isUnlocked;
  }

  private async tryAutoLogin(): Promise<void> {
    if (!this.featureFlagsService.isEnabled(FEATURE_FLAGS.PERSIST_RAW_DEK)) return;
    const rawDek = await this.metadataKeyRepository.get<number[]>(STORAGE_KEYS.DEK_RAW);
    if (rawDek === undefined) return;

    const initializationDeferred = this.ensureDatabaseInitializationDeferred();

    try {
      const unlocked = await this.authStore.unlockWithRawDek(rawDek);
      if (!unlocked) return;

      await this.authStore.loginByAuto();
      await initializationDeferred.promise;

      console.log('🔓 Auto-login successful (feature flag enabled)');
    } catch (error) {
      console.warn('⚠️ Auto-login failed:', error);
      await this.metadataKeyRepository.delete(STORAGE_KEYS.DEK_RAW);
    } finally {
      this.clearDatabaseInitializationDeferred(initializationDeferred);
    }
  }

  private async loadApplicationData(): Promise<void> {
    console.log('🔄 Loading application data...');

    const currenciesStore = this.injector.get(CurrenciesStore);
    const accountsStore = this.injector.get(AccountsStore);
    const categoriesStore = this.injector.get(CategoriesStore);

    const results = await Promise.allSettled([
      currenciesStore.loadCurrencies(),
      accountsStore.loadAccounts(),
      categoriesStore.loadCategories(),
    ]);

    for (const result of results) {
      if (result.status === 'rejected') {
        this.appErrorHandler.handle(result.reason, 'common.notifications.unexpected_error');
      }
    }
  }

  private notifyRecoveredMissingOpfsDatabase(wasDiscarded: boolean | null): void {
    const detail = this.translateService.instant(
      wasDiscarded
        ? 'common.notifications.opfs_recovered_after_discard_message'
        : 'common.notifications.opfs_recovered_message',
    );

    this.notificationService.warn(detail, this.translateService.instant('common.notifications.opfs_recovered_header'), {
      sticky: true,
      life: 0,
      key: 'center',
    });
  }

  async initializeCleanDatabase(): Promise<void> {
    if (!this.appStore.isUnlocked()) {
      throw new DekError('initialize-database', 'Application must be unlocked before reinitializing database');
    }

    try {
      await this.databaseService.initialize({
        runMigrations: true,
      });
      this.dbSyncService.startPeriodicSync();
      await this.loadApplicationData();
      this.appStore.markDatabaseInitialized();
    } catch (error) {
      await this.handleInitializationError(error);
      throw error;
    }
  }

  private async handleInitializationError(error: unknown): Promise<void> {
    const errorMessage = error instanceof Error ? error.message : 'Unknown initialization error';
    console.error('❌ Database initialization failed:', errorMessage);

    const errorCode =
      typeof error === 'object' && error !== null && 'code' in error && typeof error.code === 'string'
        ? error.code
        : undefined;
    const shouldOfferDatabaseReinitialize = errorCode === DATABASE_MISSING_RECOVERY_SNAPSHOT_ERROR_CODE;
    if (!shouldOfferDatabaseReinitialize) {
      this.authStore.lock();
    }

    await this.router.navigate(['/error'], {
      queryParams: {
        message: 'Failed to initialize the application',
        details: errorMessage,
        action: shouldOfferDatabaseReinitialize ? ERROR_ACTION_REINITIALIZE_DATABASE : undefined,
      },
    });
  }

  private ensureDatabaseInitializationDeferred(): Deferred<void> {
    if (!this.databaseInitializationDeferred) {
      this.databaseInitializationDeferred = createDeferred<void>();
    }

    return this.databaseInitializationDeferred;
  }

  private clearDatabaseInitializationDeferred(deferred: Deferred<void>): void {
    if (this.databaseInitializationDeferred === deferred) {
      this.databaseInitializationDeferred = undefined;
    }
  }
}
